import { useEffect, useState } from "react";
import {
  applyGlobalCombinationValues,
  generateCombinations,
  mergeCombinations,
  type CombinationOption,
  type ProductCombinationInput,
} from "shared/services/api/combinacoes.api";
import { formatVariationValue } from "shared/utils/variationValue";
import { PriceInput } from "../../inputs/PriceInput";
import styles from "./Combinacoes.module.scss";

interface Props {
  selectedVariationOptions: Record<string, CombinationOption[]>;
  combinations: ProductCombinationInput[];
  /** Combinations removed earlier; a matching row gets its price back. */
  inactiveCombinations?: ProductCombinationInput[];
  setCombinations: React.Dispatch<
    React.SetStateAction<ProductCombinationInput[]>
  >;
}

export const Combinacoes = ({
  selectedVariationOptions,
  combinations,
  setCombinations,
  inactiveCombinations,
}: Props) => {
  const [globalPrice, setGlobalPrice] = useState(0);
  const [globalStock, setGlobalStock] = useState(0);
  const [message, setMessage] = useState("");

  useEffect(() => {
    const allGroups = Object.values(selectedVariationOptions);
    const optionGroups = allGroups.filter((options) => options.length > 0);

    setMessage(
      optionGroups.length < allGroups.length
        ? "Variações sem nenhum valor marcado foram ignoradas."
        : "",
    );
    setCombinations((current) =>
      mergeCombinations(
        generateCombinations(optionGroups),
        current,
        inactiveCombinations,
      ),
    );
  }, [selectedVariationOptions, setCombinations, inactiveCombinations]);

  // Current label and unit of each selected value; snapshots can be older
  // (decimal places or unit changed since the combination was saved).
  // Só em produtos já salvos: o backend gera o SKU interno ao gravar. Serve
  // para o lojista usar o nosso em vez de criar o dele.
  const mostrarSkuInterno = combinations.some(
    (combination) => combination.internalSku,
  );

  const valueLabels = new Map<string, string>();
  for (const options of Object.values(selectedVariationOptions)) {
    for (const option of options) {
      valueLabels.set(
        `${option.variationDefinitionId}:${option.variationValueId ?? option.value}`,
        formatVariationValue(option.label, option.unit),
      );
    }
  }

  function applyGlobalValues() {
    if (globalPrice < 0 || globalStock < 0) {
      setMessage("Preço e estoque devem ser não negativos.");
      return;
    }
    setCombinations((current) =>
      applyGlobalCombinationValues(current, globalPrice, globalStock),
    );
  }

  function setSell(index: number, sell: boolean) {
    setCombinations((current) =>
      current.map((combination, combinationIndex) =>
        combinationIndex === index ? { ...combination, sell } : combination,
      ),
    );
  }

  function updateCombination(
    index: number,
    field: "price" | "stock",
    value: number,
  ) {
    setCombinations((current) =>
      current.map((combination, combinationIndex) =>
        combinationIndex === index
          ? { ...combination, [field]: value }
          : combination,
      ),
    );
  }

  return (
    <section className={styles.container}>
      <header>
        <h2>Preço e Estoque</h2>
        <p>
          Defina preço e estoque de cada combinação. Desligue "Vender" nas que
          a loja não oferece: elas não aparecem para o cliente.
        </p>
      </header>

      {message && <p className={styles.message}>{message}</p>}

      {Object.keys(selectedVariationOptions).length === 0 ? (
        <p className={styles.empty}>
          Selecione as variações e seus valores na aba anterior.
        </p>
      ) : (
        <>
          {combinations.length > 0 && (
            <>
              <div className={styles.globalControls}>
                <label>
                  Preço global (R$)
                  <PriceInput
                    value={globalPrice}
                    onChange={setGlobalPrice}
                    customClass={styles.priceInput}
                  />
                </label>
                <label>
                  Estoque global
                  <input
                    type="number"
                    min={0}
                    step={1}
                    value={globalStock}
                    onChange={(event) =>
                      setGlobalStock(Number(event.target.value))
                    }
                  />
                </label>
                <button
                  type="button"
                  className={styles.secondaryButton}
                  onClick={applyGlobalValues}
                >
                  Aplicar a todas
                </button>
              </div>

              <div className={styles.tableWrapper}>
                <table>
                  <thead>
                    <tr>
                      <th>Vender</th>
                      <th>Combinação</th>
                      {mostrarSkuInterno && <th>SKU interno</th>}
                      <th>SKU do lojista</th>
                      <th>Preço (R$)</th>
                      <th>Estoque</th>
                    </tr>
                  </thead>
                  <tbody>
                    {combinations.map((combination, index) => (
                      <tr
                        key={combination.id ?? index}
                        className={
                          combination.sell === false ? styles.notSold : undefined
                        }
                      >
                        <td className={styles.sellCell}>
                          <input
                            type="checkbox"
                            checked={combination.sell !== false}
                            onChange={(event) =>
                              setSell(index, event.target.checked)
                            }
                            aria-label="Vender esta combinação"
                          />
                        </td>
                        <td>
                          {combination.values
                            .map(
                              (value) =>
                                valueLabels.get(
                                  `${value.variationDefinitionId}:${value.variationValueId ?? value.valueSnapshot}`,
                                ) ?? value.labelSnapshot,
                            )
                            .join(" / ")}
                        </td>
                        {mostrarSkuInterno && (
                          <td className={styles.internalSku}>
                            {combination.internalSku ?? ""}
                          </td>
                        )}
                        <td>
                          <input
                            value={combination.sellerSku ?? ""}
                            onChange={(event) =>
                              setCombinations((current) =>
                                current.map((item, itemIndex) =>
                                  itemIndex === index
                                    ? { ...item, sellerSku: event.target.value }
                                    : item,
                                ),
                              )
                            }
                          />
                        </td>
                        <td>
                          <PriceInput
                            value={combination.price}
                            onChange={(cents) =>
                              updateCombination(index, "price", cents)
                            }
                            customClass={styles.priceInput}
                          />
                        </td>
                        <td>
                          <input
                            type="number"
                            min={0}
                            step={1}
                            value={combination.stock}
                            onChange={(event) =>
                              updateCombination(
                                index,
                                "stock",
                                Number(event.target.value),
                              )
                            }
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </>
      )}
    </section>
  );
};
